/* eslint-disable camelcase */
import React, { useMemo } from 'react';

import { Bone } from '@lightspeed/flame/Bone';
import { Button } from '@lightspeed/flame/Button';
import { Card } from '@lightspeed/flame/Card';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Text, TextLink } from '@lightspeed/flame/Text';
import { useQuery } from '@tanstack/react-query';

import { BOLD_FONT, dateFormatOptions, TEN_MIN_STALE_TIME } from '.';
import { CommitDiffLogsResponse, GitHubCommitApiResponse } from './common.types';

type DiffListProps = {
  firstHash: string;
  secondHash?: string;
};

const getPage = (totalCommits: number) => {
  const PAGE_SIZE = 10;
  let PAGE = 1;

  if (totalCommits === 0) {
    return { per_page: PAGE_SIZE, page: PAGE };
  }

  if (totalCommits % PAGE_SIZE === 0) {
    PAGE = totalCommits / PAGE_SIZE;
  }

  if (totalCommits % PAGE_SIZE !== 0) {
    PAGE = Math.floor(totalCommits / PAGE_SIZE) + 1;
  }

  return { per_page: PAGE_SIZE, page: PAGE };
};

const getCommitLogDiff = ({
  firstHash,
  secondHash,
  per_page,
  page,
}: DiffListProps & { per_page: number; page: number }) =>
  fetch(
    `/staff/deployment-info/compare?first_sha=${firstHash}&second_sha=${secondHash}&page=${page}&per_page=${per_page}`
  ).then((res) => (res.ok ? res.json() : [])) as Promise<CommitDiffLogsResponse>;

export default function DiffList({ firstHash, secondHash = 'develop-retail' }: DiffListProps) {
  const {
    data: commitLogDiffData,
    isFetching,
    refetch: refetchTotalDiffCount,
  } = useQuery({
    queryKey: [`${firstHash}diff${secondHash}-short`],
    queryFn: () => getCommitLogDiff({ firstHash, secondHash, per_page: 1, page: 1 }),
    staleTime: TEN_MIN_STALE_TIME,
  });

  const {
    data: detailedCommitLogDiff,
    isFetching: isFetchingFullQuery,
    refetch: refetchCommitDiffs,
  } = useQuery({
    queryKey: [`${firstHash}diff${secondHash}-full`],
    queryFn: () =>
      getCommitLogDiff({
        firstHash,
        secondHash,
        ...getPage(commitLogDiffData!.total_commits),
      }),
    staleTime: TEN_MIN_STALE_TIME,
    enabled: !!commitLogDiffData,
  });

  const commitsStringifiedData = JSON.stringify(detailedCommitLogDiff?.commits);

  const sortedCommitDiffLogs = useMemo(() => {
    if (detailedCommitLogDiff?.commits) {
      return [...detailedCommitLogDiff.commits].sort((first, next) => {
        if (new Date(next.commit.committer.date) === new Date(first.commit.committer.date))
          return 1;

        return new Date(next.commit.committer.date) > new Date(first.commit.committer.date)
          ? 1
          : -1;
      });
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commitsStringifiedData]);

  const isLoading = isFetching || isFetchingFullQuery;

  const renderRefetchButton = () => (
    <Button
      mt=".5rem"
      mb="1rem"
      variant="secondary"
      size="small"
      fill
      loading={isLoading}
      onClick={() => {
        refetchTotalDiffCount();
        refetchCommitDiffs();
      }}
    >
      Refresh
    </Button>
  );

  if (!detailedCommitLogDiff && isLoading) {
    return (
      <>
        {renderRefetchButton()}
        {Array(4)
          .fill('bones')
          .map((entry, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={`${entry}-${idx}`} m="1rem 0">
              <Bone height="4rem" />
            </Box>
          ))}
      </>
    );
  }

  if (detailedCommitLogDiff?.total_commits === 0)
    return (
      <>
        <Text mt=".5rem" fontWeight={600}>
          This deployment has the latest changes from {secondHash}
        </Text>
        {renderRefetchButton()}
      </>
    );

  return detailedCommitLogDiff ? (
    <>
      <Text mt=".5rem">
        This deployment does{' '}
        <span style={{ ...BOLD_FONT }}>
          not include {detailedCommitLogDiff.total_commits} commit(s)
        </span>{' '}
        from <span style={{ ...BOLD_FONT }}>{secondHash}</span>
      </Text>
      {renderRefetchButton()}
      {sortedCommitDiffLogs.map((commit) => (
        <CommitInfoRow key={commit.sha} {...commit} />
      ))}
    </>
  ) : null;
}

export const CommitInfoRow = ({ commit, author, sha }: GitHubCommitApiResponse) => {
  return (
    <Card p={2} mb={2} className="commit-info-row">
      <TextLink
        color="#0c0d0d"
        fontWeight="600"
        href={`https://github.com/merchantos/webPOS/commit/${sha}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ whiteSpace: 'pre-wrap' }}
      >
        {commit.message}
      </TextLink>
      <Flex mt={1} flexDirection="row" alignItems="center" style={{ gap: '6px' }}>
        {author ? (
          <img
            height="30px"
            width="30px"
            src={`${author.avatar_url}`}
            alt={`${commit.author.name} avatar`}
          />
        ) : null}
        <Flex
          flexDirection="column"
          justifyContent="space-between"
          fontWeight="400"
          color="gray"
          fontSize="14px"
        >
          <Text>{commit.author.email}</Text>
          <Text>
            {new Date(commit.author.date).toLocaleDateString(navigator.language, dateFormatOptions)}
          </Text>
        </Flex>
      </Flex>
    </Card>
  );
};
